<template>
  <div class="quote-intent">
    <h1 id="intent-whenGetSolar-heading" class="h4 quote-intent__title">
      When do you intend to get solar panels?
    </h1>
    <Combobox
      ref="whenGetSolar-combobox"
      v-model="selectedWhenGetSolar"
      input-id="select-intent-whenGetSolar"
      role="group"
      aria-labelledby="intent-whenGetSolar-heading"
      :options="whenGetSolarOptions"
      label="Please select"
      class="mb-7 quote-intent__when-select form-group__input-group"
    />
    <h1 id="intent-customerPreference-heading" class="h4 quote-intent__title">
      Why are you looking to get solar?
    </h1>
    <Combobox
      ref="customerPreference-combobox"
      v-model="selectedCustomerPreference"
      input-id="select-intent-customerPreference"
      role="group"
      aria-labelledby="intent-customerPreference-heading"
      :options="customerPreferenceOptions"
      label="Please select"
      class="mb-7 quote-intent__why-select form-group__input-group"
    />
    <h1 id="intent-whoSuggested-heading" class="h4 quote-intent__title">
      How did you hear about us?
    </h1>
    <Combobox
      ref="whoSuggested-combobox"
      v-model="selectedWhoSuggested"
      input-id="select-intent-whoSuggested"
      role="group"
      aria-labelledby="intent-whoSuggested-heading"
      :options="whoSuggestedOptions"
      label="Please select"
      class="mb-7 quote-intent__source-select form-group__input-group"
    />
    <InputField
      v-if="requiredFollowUpField === 'surveyorId'"
      v-model.trim="surveyorId"
      class="mb-7 quote-intent__surveyor-id-input form-group__input-group"
      name="Surveyor ID Number"
      label="Surveyor ID Number"
      maxlength="4"
      data-testid="quote-intent-surveyor-id"
    />
    <InputField
      v-if="requiredFollowUpField === 'voucherCode'"
      v-model.trim="voucherCode"
      class="mb-7 quote-intent__voucher-code-input form-group__input-group"
      name="Voucher Code"
      label="Voucher Code"
      data-testid="quote-intent-voucher-code"
    />
    <InputField
      v-if="requiredFollowUpField === 'referralCode'"
      v-model.trim="referralCode"
      class="mb-7 quote-intent__referral-code-input form-group__input-group"
      name="Referral Code"
      label="Please Enter Partner Code"
      data-testid="quote-intent-referral-code"
    />
    <InputField
      v-if="requiredFollowUpField === 'otherSuggested'"
      v-model.trim="otherSuggested"
      class="mb-7 quote-intent__other-suggested-input form-group__input-group"
      name="Other Suggested"
      label="Please enter details"
      data-testid="quote-intent-other-suggested"
    />
    <AppButton
      class="quote-intent__next-button"
      full-width
      size="large"
      :disabled="!canGoToNextStep"
      data-testid="quote-intent-continue"
      @click="next"
    >
      <span>Continue</span>
      <span class="icon quote-intent__next-button--icon icon-arrow-right" />
    </AppButton>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue"
import Combobox from "@soenergy/frontend-library/src/components/Combobox.vue"
import AppButton from "@soenergy/frontend-library/src/components/AppButton.vue"
import InputField from "@soenergy/frontend-library/src/components/InputField.vue"
import TrackingService from "@soenergy/frontend-library/src/services/TrackingService"
import { mapMutations, mapGetters } from "vuex"

export interface IntentOption {
  label: string
  value: string
}

// the payload sends the value as installation_timeframe, so keep them stable
const WHEN_GET_SOLAR_OPTIONS: IntentOption[] = [
  { label: "As soon as possible", value: "now" },
  { label: "In the next 3 months", value: "3_months" },
  { label: "In 6 months or later", value: "6_months" },
  { label: "I’m not sure / just browsing", value: "not_sure" },
]

// values reuse the legacy CMS strings so customer_preference is unchanged
const CUSTOMER_PREFERENCE_OPTIONS: IntentOption[] = [
  {
    label: "Biggest energy bill savings / maximum generation",
    value: "Maximum generation",
  },
  { label: "Shortest payback period", value: "Shortest pay back" },
  { label: "To charge my EV", value: "Charge EV" },
  { label: "Lowest cost", value: "Lowest cost" },
]

// legacy CMS strings and order; FOLLOW_UP_FIELDS below decides which of these
// ask a follow-up question
const WHO_SUGGESTED_OPTIONS: IntentOption[] = [
  {
    label: "I’m an existing So Energy customer",
    value: "Existing So Energy customer",
  },
  { label: "Google / search engine", value: "Google" },
  { label: "Email", value: "Email" },
  { label: "Personal recommendation", value: "Personal recommendation" },
  { label: "Social media", value: "Social media" },
  { label: "Advert", value: "Advert" },
  { label: "Home Survey", value: "Home Survey" },
  { label: "Voucher Code", value: "Voucher Code" },
  { label: "Referral Code", value: "Referral Code" },
  { label: "Other", value: "Other" },
]

type FollowUpField =
  | "surveyorId"
  | "voucherCode"
  | "referralCode"
  | "otherSuggested"

// the only sources that ask a follow-up question, mapped to the field that
// holds the answer; the template, the continue guard and the commits all read
// the mapping from here
const FOLLOW_UP_FIELDS: Record<string, FollowUpField | undefined> = {
  "Home Survey": "surveyorId",
  "Voucher Code": "voucherCode",
  "Referral Code": "referralCode",
  Other: "otherSuggested",
}

export default defineComponent({
  components: {
    AppButton,
    Combobox,
    InputField,
  },
  emits: ["go-forward"],
  data() {
    return {
      selectedWhenGetSolar: null as IntentOption | null,
      selectedCustomerPreference: null as IntentOption | null,
      selectedWhoSuggested: null as IntentOption | null,
      surveyorId: null as string | null,
      voucherCode: null as string | null,
      referralCode: null as string | null,
      otherSuggested: null as string | null,
      whenGetSolarOptions: WHEN_GET_SOLAR_OPTIONS,
      customerPreferenceOptions: CUSTOMER_PREFERENCE_OPTIONS,
      whoSuggestedOptions: WHO_SUGGESTED_OPTIONS,
    }
  },
  computed: {
    ...mapGetters([
      "getWhenGetSolar",
      "getCustomerPreference",
      "getWhoSuggested",
      "getSurveyorId",
      "getVoucherCode",
      "getReferralCode",
      "getOtherSuggested",
    ]),
    requiredFollowUpField(): FollowUpField | null {
      const source = this.selectedWhoSuggested?.value
      return source ? FOLLOW_UP_FIELDS[source] ?? null : null
    },
    canGoToNextStep(): boolean {
      if (
        !this.selectedWhenGetSolar ||
        !this.selectedCustomerPreference ||
        !this.selectedWhoSuggested
      ) {
        return false
      }
      const field = this.requiredFollowUpField
      return !field || Boolean(this[field])
    },
  },
  created() {
    this.selectedWhenGetSolar = this.getWhenGetSolar
    this.selectedCustomerPreference = this.getCustomerPreference
    this.selectedWhoSuggested = this.getWhoSuggested
    this.surveyorId = this.getSurveyorId
    this.voucherCode = this.getVoucherCode
    this.referralCode = this.getReferralCode
    this.otherSuggested = this.getOtherSuggested
  },
  methods: {
    ...mapMutations({
      setWhenGetSolar: "SET_WHEN_GET_SOLAR",
      setCustomerPreference: "SET_CUSTOMER_PREFERENCE",
      setWhoSuggested: "SET_WHO_SUGGESTED",
      setSurveyorId: "SET_SURVEYOR_ID",
      setVoucherCode: "SET_VOUCHER_CODE",
      setReferralCode: "SET_REFERRAL_CODE",
      setOtherSuggested: "SET_OTHER_SUGGESTED",
    }),
    next() {
      const whenGetSolar = this.selectedWhenGetSolar
      const customerPreference = this.selectedCustomerPreference
      const whoSuggested = this.selectedWhoSuggested
      if (!whenGetSolar || !customerPreference || !whoSuggested) return

      this.setWhenGetSolar(whenGetSolar)
      this.setCustomerPreference(customerPreference)
      this.setWhoSuggested(whoSuggested)
      const field = this.requiredFollowUpField
      // legacy commit semantics: surveyor ID and voucher code always overwrite,
      // referral code and other details only write when their option is selected
      this.setSurveyorId(field === "surveyorId" ? this.surveyorId : null)
      this.setVoucherCode(field === "voucherCode" ? this.voucherCode : null)
      if (field === "referralCode") {
        this.setReferralCode(this.referralCode)
      }
      if (field === "otherSuggested") {
        this.setOtherSuggested(this.otherSuggested)
      }
      TrackingService.sendEvent("Solar:Intent:Continue")
      TrackingService.sendEvent(`Solar:IntentWhy:${customerPreference.value}`)
      this.$emit("go-forward")
    },
  },
})
</script>

<style lang="scss" scoped>
.quote-intent {
  @include md {
    max-width: $width-7;
    margin: 0 auto;
  }

  &__title {
    text-transform: none;
    font-weight: $weight-medium;
  }

  &__when-select,
  &__why-select,
  &__source-select {
    background-color: $white;
  }

  &__next-button {
    margin-top: $space-5;

    &--icon.icon {
      font-weight: $weight-medium;
      font-size: $space-5;
      margin-left: $space-2;
    }
  }
}
</style>
